import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useChangePassword, useForgotPassword } from "@/features/auth/repositories/auth-repository";
import { ChangePasswordSchema, TChangePasswordSchema } from "@/features/settings/forms/schema";

import Button from "@/components/buttons/Button";
import TextInput from "@/components/form-controls/TextInput";


type ChangePasswordProps = {
    email?: string
}

/**
 * ZCT has no "old password" exchange: `POST /api/v1/users/update-password` only accepts a
 * password paired with a one-time code, and `UserServiceImplementor#updatePassword` rejects
 * anything without a matching unused `VerificationToken`. The code is issued by
 * `POST /api/v1/users/forgot-password`, so a change is always request-code then submit.
 */
const ChangePassword = ({ email }: ChangePasswordProps) => {
    const [codeRequested, setCodeRequested] = useState(false);

    const { mutate: requestCode, isPending: isRequesting } = useForgotPassword(
        () => setCodeRequested(true)
    );

    const { register, handleSubmit, reset, formState: { errors } } = useForm({
        resolver: yupResolver(ChangePasswordSchema),
        defaultValues: { otp: "", password: "", repeatPassword: "" }
    });

    const { mutate: changePassword, isPending } = useChangePassword(() => {
        setCodeRequested(false);
        reset();
    });

    const submit = (data: TChangePasswordSchema) => {
        if (!email) return;

        changePassword({
            username: email,
            password: data.password,
            otp: data.otp
        });
    }

    return (
        <section className="border-t border-t-gray-200 pt-6">
            <h2 className="text-lg font-semibold">Password</h2>
            <p className="text-xs text-secondary-300 mb-4">
                ZCT confirms a password change with a one-time code. Request the code, then enter
                it here with your new password.
            </p>

            {
                !codeRequested ? (
                    <Button
                        type="button"
                        intent="secondary"
                        loading={isRequesting}
                        disabled={!email}
                        onClick={() => email && requestCode(email)}
                    >
                        Send me a code
                    </Button>
                ) : (
                    <form onSubmit={handleSubmit(submit)} className="grid grid-cols-2 gap-4">
                        <TextInput
                            label="Verification Code"
                            placeholder="enter the code sent to your email"
                            className="col-span-2"
                            error={errors.otp?.message?.toString()}
                            register={register("otp")}
                            required
                        />

                        <TextInput
                            label="New Password"
                            type="password"
                            placeholder="*******"
                            error={errors.password?.message?.toString()}
                            register={register("password")}
                            required
                        />

                        <TextInput
                            label="Confirm New Password"
                            type="password"
                            placeholder="*******"
                            error={errors.repeatPassword?.message?.toString()}
                            register={register("repeatPassword")}
                            required
                        />

                        <div className="col-span-2 flex justify-end space-x-2">
                            <Button
                                type="button"
                                intent="default"
                                onClick={() => { setCodeRequested(false); reset(); }}
                            >
                                Cancel
                            </Button>

                            <Button
                                type="submit"
                                className="min-w-32"
                                intent="primary"
                                loading={isPending}
                            >
                                Change Password
                            </Button>
                        </div>
                    </form>
                )
            }
        </section>
    )
}

export default ChangePassword
